// Build "Add to calendar" links/files for calendar events.
// Event times are entered in Arizona (America/Phoenix), which has NO daylight saving,
// so it is a fixed UTC-7 offset year-round. We convert local Arizona time -> UTC.

const AZ_OFFSET_HOURS = 7; // Arizona is UTC-7 (MST, no DST)
const DEFAULT_DURATION_MIN = 60;

const pad = (n) => String(n).padStart(2, "0");

function parseTime(time) {
  // Accepts "HH:MM" (24h) or "H:MM AM/PM". Returns {hh, mm} or null.
  if (!time) return null;
  const t = time.trim().toUpperCase();
  let m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/);
  if (!m) {
    m = t.match(/^(\d{1,2})\s*(AM|PM)$/);
    if (m) return normalize(parseInt(m[1], 10), 0, m[2]);
    return null;
  }
  return normalize(parseInt(m[1], 10), parseInt(m[2], 10), m[3]);
}
function normalize(hh, mm, ap) {
  if (ap === "PM" && hh < 12) hh += 12;
  if (ap === "AM" && hh === 12) hh = 0;
  return { hh, mm };
}

// Returns UTC Date for an Arizona-local date+time.
function azToUtc(date, time) {
  const [y, mo, d] = date.split("-").map((x) => parseInt(x, 10));
  const t = parseTime(time);
  const hh = t ? t.hh : 9;
  const mm = t ? t.mm : 0;
  // Arizona local -> UTC by adding the offset.
  return new Date(Date.UTC(y, mo - 1, d, hh + AZ_OFFSET_HOURS, mm, 0));
}

function fmtUtc(dt) {
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
}
function fmtDateOnly(date) {
  return date.replace(/-/g, "");
}
function nextDay(date) {
  const [y, mo, d] = date.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, mo - 1, d + 1));
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}`;
}

function eventBits(e) {
  const hasTime = !!e.time;
  const title = e.title || "Event";
  const details = [e.description || "", e.link ? `\nLink: ${e.link}` : ""].join("").trim();
  const location = e.link || "";
  if (hasTime) {
    const start = azToUtc(e.date, e.time);
    const end = new Date(start.getTime() + DEFAULT_DURATION_MIN * 60000);
    return { title, details, location, allDay: false, start: fmtUtc(start), end: fmtUtc(end) };
  }
  return { title, details, location, allDay: true, start: fmtDateOnly(e.date), end: nextDay(e.date) };
}

export function googleCalendarUrl(e) {
  const b = eventBits(e);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: b.title,
    dates: `${b.start}/${b.end}`,
    details: b.details,
    location: b.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function escapeICS(s) {
  return String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

export function buildICS(e) {
  const b = eventBits(e);
  const uid = `${(e.id || Math.random().toString(36).slice(2))}@helpsolvethecase.com`;
  const stamp = fmtUtc(new Date());
  const dt = b.allDay
    ? [`DTSTART;VALUE=DATE:${b.start}`, `DTEND;VALUE=DATE:${b.end}`]
    : [`DTSTART:${b.start}`, `DTEND:${b.end}`];
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Help Solve The Case//Calendar//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    ...dt,
    `SUMMARY:${escapeICS(b.title)}`,
    `DESCRIPTION:${escapeICS(b.details)}`,
    b.location ? `LOCATION:${escapeICS(b.location)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

export function downloadICS(e) {
  const ics = buildICS(e);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(e.title || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
